import { useState, useEffect, useRef } from "react";
import "./Header.css";
import { useNavigate } from "react-router-dom";
import "./../Header/profile.css";
import "./../Header/logout-popup.css";
import { CiWarning } from "react-icons/ci";
import { FaUser, FaSignOutAlt, FaBars, FaBell, FaLock, FaEye, FaEyeSlash } from "react-icons/fa";

const Modal = ({ isOpen, onClose, children }) => {
  if (!isOpen) return null;
  const user = JSON.parse(localStorage.getItem("user"));
  return (
    <div className="modal-overlay">
      {user.role === "admin" ? (
        <div className="modal-content">
          {/* Close icon in the top-right corner */}
          <span className="close-icon" onClick={onClose}>
            ✖
          </span>
          {children}
        </div>
      ) : (
        <div className="employee-manager-teamleader-profile-container-modal-content ">
          {/* Close icon in the top-right corner */}
          <span className="close-icon" onClick={onClose}>
            ✖
          </span>
          {children}
        </div>
      )}
    </div>
  );
};
const getProfileImage = (profilePath) => {
  if (!profilePath) {
    return "/images/icon.png";
  }

  if (typeof profilePath === "string") {
    // If it's a full URL containing 127.0.0.1 or localhost
    if (profilePath.includes("127.0.0.1") || profilePath.includes("localhost")) {
      // If we are running on the live website, replace local domain with the live domain
      if (window.location.hostname !== "localhost" && window.location.hostname !== "127.0.0.1") {
        return profilePath.replace(/^https?:\/\/[^\/]+/, "https://aligndesk.dropyourbusiness.com");
      }
    }

    if (profilePath.startsWith("../images/")) {
      return profilePath.replace("../images/", "/images/");
    }
  }

  return profilePath;
};
const Header = ({ toggleSidebar }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [role, setRole] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false); // State to control the confirmation modal
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  const notifRef = useRef(null);

  // Notification states
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [isNotifModalOpen, setIsNotifModalOpen] = useState(false);

  const fetchNotifications = async () => {
    try {
      const user = JSON.parse(localStorage.getItem("user")) || {};
      const userProfile = JSON.parse(localStorage.getItem("userProfile")) || {};

      // Collect all possible IDs (Users table ID, Employee ID, Admin ID)
      const possibleIds = [
        user.id,
        user.employee_id,
        userProfile.EMP_Id,
        userProfile.Admin_id,
        userProfile.id
      ].filter(Boolean);

      const uniqueIds = [...new Set(possibleIds)];

      if (uniqueIds.length > 0) {
        const idString = uniqueIds.join(',');
        const response = await fetch(`/api/notifications/${idString}`);
        const data = await response.json();
        if (data.notifications) {
          setNotifications(data.notifications);
          setUnreadCount(data.unread_count || 0);
        }
      }
    } catch (err) {
      console.error("Error fetching notifications", err);
    }
  };

  const handleMarkAsRead = async (notificationId) => {
    // Optimistic UI update for instant feedback
    setNotifications(prev => prev.map(n => n.id === notificationId ? { ...n, is_read: 1 } : n));
    setUnreadCount(prev => Math.max(0, prev - 1));

    try {
      const response = await fetch(`/api/notifications/read/${notificationId}`, {
        method: "PUT",
      });
      if (response.ok) {
        fetchNotifications(); // Sync with DB
      } else {
        fetchNotifications(); // Revert on failure
      }
    } catch (err) {
      console.error("Error marking notification as read", err);
      fetchNotifications(); // Revert on failure
    }
  };

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 10000);
    return () => clearInterval(interval);
  }, []);

  // Close dropdown if clicked outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Close profile dropdown
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
      // Close notifications dropdown
      if (notifRef.current && !notifRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);
  // Fetch user profile immediately when the page loads
  const fetchUserProfile = async () => {
    // 1. First, check if we have cached profile data in localStorage
    const cachedProfile = localStorage.getItem("userProfile");
    if (cachedProfile) {
      setUserData(JSON.parse(cachedProfile));
      // We have cache, so don't show blocking loading spinner
      setLoading(false);
    } else {
      setLoading(true);
    }
    setError(null);

    try {
      const user = JSON.parse(localStorage.getItem("user"));
      if (!user || !user.id || !user.role) {
        throw new Error("User not found in localStorage.");
      }
      let apiUrl = "";
      if (user.role === "admin") {
        apiUrl = `/api/admin/${user.id}`;
      } else if (
        user.role === "employee" ||
        user.role === "manager" ||
        user.role === "Team Leader"
      ) {
        apiUrl = `/api/emp/${user.id}`;
      } else {
        throw new Error("Invalid role.");
      }
      console.log("Fetching from:", apiUrl);
      const response = await fetch(apiUrl);
      if (!response.ok) {
        if (response.status === 404) {
          setError("Profile not found.");
        } else {
          setError(`Failed to fetch user data. Status: ${response.status}`);
        }
        return;
      }
      const data = await response.json();
      console.log("API Response Data:", data);
      const profile = data.admin || data.employee || null;

      // 2. Save/Update cache in localStorage
      if (profile) {
        setUserData(profile);
        localStorage.setItem("userProfile", JSON.stringify(profile));
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (user && user.role) {
      setRole(user.role);
      fetchUserProfile(); // No argument needed
    } else {
      setRole("Role not found.");
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const openProfileModal = () => {
    console.log("Opening Profile Modal...");
    setIsModalOpen(true);
    setDropdownOpen(false); // Close dropdown when opening profile
    fetchUserProfile();
  };

  // Handle click on the logout button
  const handleLogoutClick = () => {
    console.log("Logout button clicked"); // Debugging line
    setShowLogoutConfirm(true); // Set the state to show the confirmation modal
  };
  // Actual logout function
  const handleLogout = () => {
    localStorage.clear();
    setDropdownOpen(false);
    navigate("/");
  };
  // When the user confirms the logout action
  const handleConfirmLogout = () => {
    console.log("User confirmed logout"); // Debugging line
    handleLogout(); // Proceed with logout
    setShowLogoutConfirm(false); // Hide the confirmation modal
  };
  // When the user cancels the logout action
  const handleCancelLogout = () => {
    console.log("User cancelled logout"); // Debugging line
    setShowLogoutConfirm(false); // Close the confirmation modal
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const user = JSON.parse(localStorage.getItem("user"));
    if (!user || !user.id || !user.role) return;

    const formData = new FormData();
    let uploadApiUrl = "";

    if (user.role === "admin") {
      formData.append("admin_image", file);
      uploadApiUrl = `/api/admin/add-image/${user.id}`;
    } else {
      formData.append("employee_image", file);
      uploadApiUrl = `/api/emp/add-image/${user.id}`;
    }

    setLoading(true);
    try {
      const response = await fetch(uploadApiUrl, {
        method: "POST",
        body: formData,
      });

      if (response.ok) {
        // Refresh the profile to show the new image
        fetchUserProfile();
        alert("Image uploaded successfully!");
      } else {
        const errData = await response.json().catch(() => ({}));
        alert(errData.error || errData.message || `Failed to upload image. Server returned status: ${response.status}`);
      }
    } catch (err) {
      console.error("Image upload error:", err);
      alert("An error occurred while uploading the image: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async () => {
    setPasswordError("");
    setPasswordSuccess("");

    if (!newPassword || newPassword.length < 6) {
      setPasswordError("Password must be at least 6 characters.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError("Passwords do not match.");
      return;
    }

    setShowPasswordConfirm(true);
  };

  const executePasswordChange = async () => {
    setShowPasswordConfirm(false);
    setLoading(true);
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      let url = "";
      if (user.role === "admin") {
        url = `/api/admin/reset-password/${user.id}`;
      } else {
        url = `/api/employee/reset-password/${user.id}`;
      }

      const response = await fetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ new_password: newPassword }),
      });

      if (response.ok) {
        setPasswordSuccess("Password updated successfully!");
        setNewPassword("");
        setConfirmPassword("");
        setTimeout(() => {
          setShowPasswordChange(false);
          setPasswordSuccess("");
        }, 2000);
      } else {
        setPasswordError("Failed to update password.");
      }
    } catch (err) {
      console.error(err);
      setPasswordError("An error occurred while updating the password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="header">
        <div className="header-left">
          <FaBars className="hamburger-menu" onClick={toggleSidebar} />
        </div>
        <div
          className="header-right"
          ref={dropdownRef}
        >


          <div className="greeting-container" onClick={() => { setDropdownOpen((prev) => !prev); setShowNotifications(false); }} style={{ cursor: "pointer" }}>
            {/* Display Name */}
            <span className="user-name">
              {role === "admin"
                ? userData?.admin_fullname
                  ?.split(" ")
                  .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                  .join(" ")
                : `${userData?.employee_first_name?.charAt(0).toUpperCase() +
                userData?.employee_first_name?.slice(1)
                } 
         ${userData?.employee_last_name?.charAt(0).toUpperCase() +
                userData?.employee_last_name?.slice(1)
                }`}
              ,
            </span>
            {/* Display Designation Only for Employee, Manager, Team Leader */}
            {role === "admin" ? (
              <span className="designation">
                {role.charAt(0).toUpperCase() + role.slice(1)}
              </span>
            ) : (
              <span className="designation">
                {userData?.employee_type
                  ? userData.employee_type.charAt(0).toUpperCase() +
                  userData.employee_type.slice(1)
                  : "No Designation"}
              </span>
            )}
          </div>
          <div
            className="profile-icon"
            onClick={() => { setDropdownOpen((prev) => !prev); setShowNotifications(false); }}
            style={{ cursor: "pointer" }}
          >
            <img
              src={
                role === "admin"
                  ? getProfileImage(userData?.admin_profile || userData?.A_Img)
                  : getProfileImage(userData?.employee_profile || userData?.EMP_Image)
              }
              alt="Profile"
              className="profile-img"
              onError={(e) => { e.target.onerror = null; e.target.src = "/images/icon.png"; }}
            />
          </div>
          {/* Notification Bell */}
          <div
            ref={notifRef}
            style={{ position: "relative", cursor: "pointer", fontSize: "22px", marginTop: "5px" }}
            onClick={(e) => { e.stopPropagation(); setShowNotifications(!showNotifications); setDropdownOpen(false); }}
          >
            <FaBell />
            {unreadCount > 0 && (
              <span style={{
                position: "absolute", top: "-5px", right: "-5px", background: "red", color: "white",
                borderRadius: "50%", padding: "2px 6px", fontSize: "12px", fontWeight: "bold"
              }}>
                {unreadCount}
              </span>
            )}
            {showNotifications && (
              <div className="dropdown notifications-dropdown" style={{ minWidth: "280px", right: 0, padding: "10px", zIndex: 1000 }}>
                <style>{`
                  .notifications-list::-webkit-scrollbar {
                    display: none !important;
                    width: 0px !important;
                    height: 0px !important;
                  }
                  .notifications-list {
                    -ms-overflow-style: none !important;
                    scrollbar-width: none !important;
                  }
                  .notifications-dropdown {
                    overflow: hidden !important;
                  }
                `}</style>
                <h4 style={{ margin: "0 0 10px 0", borderBottom: "1px solid #ccc", paddingBottom: "5px" }}>Notifications</h4>
                <div className="notifications-list" style={{ maxHeight: "300px", overflowY: "auto" }}>
                  {notifications.length === 0 ? (
                    <p style={{ margin: 0, fontSize: "14px", color: "gray" }}>No notifications</p>
                  ) : (
                    notifications.map(notif => (
                      <div
                        key={notif.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedNotification(notif);
                          setIsNotifModalOpen(true);
                          setShowNotifications(false);
                        }}
                        style={{
                          padding: "12px 10px",
                          borderBottom: "1px solid rgba(255,255,255,0.1)",
                          fontSize: "14px",
                          color: notif.is_read === 0 ? "#ffffff" : "#9ca3af",
                          cursor: "pointer",
                          transition: "all 0.2s",
                          background: notif.is_read === 0 ? "rgba(59, 130, 246, 0.1)" : "transparent",
                          fontWeight: notif.is_read === 0 ? "bold" : "normal",
                          borderLeft: notif.is_read === 0 ? "3px solid #3b82f6" : "3px solid transparent"
                        }}
                        onMouseEnter={(e) => e.target.style.background = notif.is_read === 0 ? "rgba(59, 130, 246, 0.2)" : "rgba(255,255,255,0.05)"}
                        onMouseLeave={(e) => e.target.style.background = notif.is_read === 0 ? "rgba(59, 130, 246, 0.1)" : "transparent"}
                        title={notif.is_read === 0 ? "Click to read details" : "View previously read message"}
                      >
                        {notif.message.length > 60 ? notif.message.substring(0, 60) + "..." : notif.message}
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
          {dropdownOpen && (
            <div className="dropdown">
              <ul>
                <li onClick={openProfileModal}><FaUser className="dropdown-icon" /> My Profile</li>
                <li onClick={() => { setDropdownOpen(false); setShowPasswordChange(true); }}><FaLock className="dropdown-icon" /> Change Password</li>
                <li onClick={handleLogoutClick} className="logout-item"><FaSignOutAlt className="dropdown-icon" /> Logout</li>
              </ul>
            </div>
          )}
        </div>
      </div>
      {/* Confirmation Modal */}
      {showLogoutConfirm && (
        <div className="logout-modal-overlay">
          <div className="modal">
            {/* Popup Header with Icon */}
            <div className="popup-header">
              <CiWarning />
              {/* Warning icon */}
              {/* Logout icon */}
              <span>Confirm Logout</span>
            </div>
            <p className="p">
              Are you sure you want to log out{" "}
              {/* Dynamically display the full name based on the role */}
              <strong>
                {role === "admin" && userData?.admin_fullname}
                {(role === "manager" ||
                  role === "employee" ||
                  role === "Team Leader") &&
                  userData?.employee_first_name && (
                    <>
                      {`${userData.employee_first_name} ${userData.employee_middle_name} ${userData.employee_last_name}`}
                    </>
                  )}
              </strong>
              ?
            </p>
            <div className="modal-buttons">
              <button onClick={handleConfirmLogout}>Yes, Logout</button>
              <button onClick={handleCancelLogout}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Password Change Confirmation Modal */}
      {showPasswordConfirm && (
        <div className="logout-modal-overlay">
          <div className="modal">
            {/* Popup Header with Icon */}
            <div className="popup-header">
              <CiWarning />
              <span>Confirm Password Change</span>
            </div>
            <p className="p">
              Are you sure you want to change your password? This action cannot be undone.
            </p>
            <div className="modal-buttons">
              <button onClick={executePasswordChange}>Yes, Change</button>
              <button onClick={() => setShowPasswordConfirm(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      >
        <h2>My Profile</h2>
        {loading && <p>Loading...</p>}
        {error && <p style={{ color: "red" }}>{error}</p>}
        {userData && (
          <div className="profile-container">
            {userData && (
              <div className="mprofile-image" style={{ position: "relative", display: "inline-block" }}>
                <label htmlFor="profile-image-upload" style={{ cursor: "pointer", position: "relative", display: "block" }}>
                  <img
                    src={
                      role === "admin"
                        ? getProfileImage(userData.admin_profile || userData.A_Img)
                        : getProfileImage(userData.employee_profile || userData.EMP_Image)
                    }
                    alt="Profile"
                    className="mprofile-img"
                    onError={(e) => { e.target.onerror = null; e.target.src = "/images/icon.png"; }}
                  />
                  <div style={{ position: "absolute", bottom: "10px", right: "10px", background: "rgba(0,0,0,0.6)", color: "#fff", borderRadius: "50%", padding: "6px", fontSize: "16px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    📷
                  </div>
                </label>
                <input
                  type="file"
                  id="profile-image-upload"
                  style={{ display: "none" }}
                  accept="image/*"
                  onChange={handleImageUpload}
                />
              </div>
            )}
            <div className="profile-details">
              {role === "admin" && (
                <>
                  <div className="detail-item" style={{ position: "relative" }}>
                    <strong>Name:</strong>
                    <span>{userData.admin_fullname}</span>
                    <span style={{ position: "absolute", right: "16px", top: "50%", transform: "translateY(-50%)", fontSize: "12px", fontWeight: "bold", color: "#94a3b8", background: "rgba(255,255,255,0.1)", padding: "4px 8px", borderRadius: "8px" }}>
                      {userData.admin_gender ? userData.admin_gender.charAt(0).toUpperCase() : ""}
                    </span>
                  </div>
                  <div className="detail-item">
                    <strong>Email:</strong> <span>{userData.admin_email}</span>
                  </div>
                  <div className="detail-item">
                    <strong>Contact:</strong>{" "}
                    <span>{userData.admin_contact}</span>
                  </div>
                </>
              )}
              {(role === "manager" ||
                role === "employee" ||
                role === "Team Leader") && (
                  <>
                    <div className="detail-item" style={{ position: "relative" }}>
                      <strong>Full Name:</strong>
                      <span>{`${userData.employee_first_name} ${userData.employee_middle_name || ""} ${userData.employee_last_name}`.replace(/\s+/g, " ").trim()}</span>
                      <span style={{ position: "absolute", right: "16px", top: "50%", transform: "translateY(-50%)", fontSize: "12px", fontWeight: "bold", color: "#94a3b8", background: "rgba(255,255,255,0.1)", padding: "4px 8px", borderRadius: "8px" }}>
                        {userData.employee_gender ? userData.employee_gender.charAt(0).toUpperCase() : ""}
                      </span>
                    </div>
                    <div className="detail-item">
                      <strong>Email:</strong>
                      <span>{userData.employee_email}</span>
                    </div>
                    <div className="detail-item">
                      <strong>Contact:</strong>{" "}
                      <span>{userData.employee_contact}</span>
                    </div>
                    <div className="detail-item">
                      <strong>Department:</strong>
                      <span>{userData.department?.department_name || "N/A"}</span>
                    </div>
                  </>
                )}
            </div>
          </div>
        )}

      </Modal>

      {/* Change Password Modal */}
      <Modal
        isOpen={showPasswordChange}
        onClose={() => {
          setShowPasswordChange(false);
          setPasswordError("");
          setPasswordSuccess("");
          setNewPassword("");
          setConfirmPassword("");
        }}
      >
        <h2>Change Password</h2>
        <div style={{ marginTop: "15px", display: "flex", flexDirection: "column", gap: "15px" }}>
          <div style={{ position: "relative" }}>
            <input
              type={showNewPassword ? "text" : "password"}
              placeholder="New Password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              style={{ width: "100%", padding: "12px", borderRadius: "8px", border: "1px solid rgba(255,255,255,0.2)", background: "rgba(255,255,255,0.05)", color: "#fff", paddingRight: "40px", boxSizing: "border-box" }}
            />
            <span
              onClick={() => setShowNewPassword(!showNewPassword)}
              style={{ position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)", cursor: "pointer", color: "#ccc", display: "flex", alignItems: "center" }}
            >
              {showNewPassword ? <FaEyeSlash /> : <FaEye />}
            </span>
          </div>
          <div style={{ position: "relative" }}>
            <input
              type={showConfirmPassword ? "text" : "password"}
              placeholder="Confirm New Password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              style={{ width: "100%", padding: "12px", borderRadius: "8px", border: "1px solid rgba(255,255,255,0.2)", background: "rgba(255,255,255,0.05)", color: "#fff", paddingRight: "40px", boxSizing: "border-box" }}
            />
            <span
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              style={{ position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)", cursor: "pointer", color: "#ccc", display: "flex", alignItems: "center" }}
            >
              {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
            </span>
          </div>
          {passwordError && <p style={{ color: "#ef4444", fontSize: "14px", margin: "0" }}>{passwordError}</p>}
          {passwordSuccess && <p style={{ color: "#2ecc71", fontSize: "14px", margin: "0" }}>{passwordSuccess}</p>}
          <button
            onClick={handlePasswordChange}
            disabled={loading}
            style={{
              background: "var(--primary)",
              color: "#fff",
              border: "none",
              padding: "12px",
              borderRadius: "8px",
              cursor: loading ? "not-allowed" : "pointer",
              marginTop: "10px",
              fontWeight: "bold",
              opacity: loading ? 0.7 : 1
            }}
          >
            {loading ? "Updating..." : "Update Password"}
          </button>
        </div>
      </Modal>
      {/* Notification Detail Modal */}
      {isNotifModalOpen && selectedNotification && (
        <div className="modal-overlay" style={{ zIndex: 2000 }}>
          <div className="modal-content" style={{ maxWidth: "500px", background: "var(--bg-card)", padding: "20px", borderRadius: "10px" }}>
            <h3 style={{ margin: "0 0 15px 0", borderBottom: "1px solid var(--border)", paddingBottom: "10px", color: "var(--text-main)" }}>
              Notification Details
            </h3>
            <p style={{ fontSize: "16px", lineHeight: "1.5", color: "var(--text-main)", marginBottom: "30px", whiteSpace: "pre-wrap" }}>
              {selectedNotification.message}
            </p>
            <div style={{ textAlign: "right" }}>
              <button
                onClick={() => {
                  if (selectedNotification.is_read === 0) {
                    handleMarkAsRead(selectedNotification.id);
                  }
                  setIsNotifModalOpen(false);
                }}
                style={{
                  background: selectedNotification.is_read === 0 ? "var(--primary)" : "#6b7280",
                  color: "#fff",
                  border: "none",
                  padding: "8px 16px",
                  borderRadius: "5px",
                  cursor: "pointer",
                  fontSize: "14px",
                  transition: "background 0.2s"
                }}
                onMouseEnter={(e) => e.target.style.background = selectedNotification.is_read === 0 ? "#2563eb" : "#4b5563"}
                onMouseLeave={(e) => e.target.style.background = selectedNotification.is_read === 0 ? "var(--primary)" : "#6b7280"}
              >
                {selectedNotification.is_read === 0 ? "Close & Mark as Read" : "Close"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
export default Header;
